import { FilterOptionKeys, GroupByMode, OrderDirection } from '@griply/types';
import {
  defaultGoalsOverviewFilterDocument,
  goalListFilters,
  goalListGroupOptions,
  goalListSortOptions,
  goalSortingModeTranslationKeys,
} from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import {
  useGoalsOverviewFilterDocument,
  useUpdateGoalsOverviewFilterDocument,
  useUpdateUserSettings,
} from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDeadline16 } from 'shared/assets/icons';
import { FilterButton } from 'shared/components/ui/filter-button';
import { useUser } from 'shared/hooks/use-user';

export const GoalsFilterButton: React.FC = () => {
  const { t } = useTranslation();

  const user = useUser();
  const filterDocument = useGoalsOverviewFilterDocument();
  const updateFilterDocument = useUpdateGoalsOverviewFilterDocument();
  const updateUserSettings = useUpdateUserSettings();

  const goals = useSortedGoals();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const tags = useSortedTags();

  const sortDirection = user?.settings?.goalSortDirection ?? OrderDirection.Asc;
  const onSortDirectionChange = (direction: OrderDirection) =>
    updateUserSettings({ goalSortDirection: direction });

  // The goal "Deadline" group reuses the task group-by key, which is labelled
  // "Date"; override it so the goal context reads "Deadline".
  const labelOverrides = useMemo(
    () => ({
      [GroupByMode.Deadline]: t('general.labels.goals.sort.options.deadline'),
    }),
    [t],
  );

  // The goal "Date" filter targets a goal's start date, so label it accordingly.
  const filterLabelOverrides = useMemo(
    () => ({ [FilterOptionKeys.Date]: t('general.labels.start-date') }),
    [t],
  );

  // The goal Deadline filter shows a calendar-deadline icon (tasks keep the flag).
  const filterIconOverrides = useMemo(
    () => ({ [FilterOptionKeys.Deadline]: CalendarDeadline16 }),
    [],
  );

  // Reset clears only the filters; sorting and grouping are left untouched.
  const onReset = () => updateFilterDocument({ filters: null });

  return (
    <FilterButton
      filterDocument={filterDocument}
      defaultFilterDocument={defaultGoalsOverviewFilterDocument}
      goals={goals}
      lifeAreas={lifeAreas}
      tags={tags}
      updateFilterDocument={updateFilterDocument}
      supportedGroupByModes={[...goalListGroupOptions]}
      supportedSortingModes={[...goalListSortOptions]}
      supportedFilters={[...goalListFilters]}
      sortingTranslationKeys={goalSortingModeTranslationKeys}
      sortDirection={sortDirection}
      onSortDirectionChange={onSortDirectionChange}
      labelOverrides={labelOverrides}
      filterLabelOverrides={filterLabelOverrides}
      filterIconOverrides={filterIconOverrides}
      title={t('context-menus.filtering.goal.title')}
      onReset={onReset}
      preventDrag
      renderAsIconButton
    />
  );
};
